import type { Phase } from "@logic/lib/design"
import { atomFamily } from "recoil"

import type { Elements } from ".."
import type { ActionFor_, ActionType, ChangeFor_ } from "./utils"

//¯¯¯¯¯¯¯//
// STORE //
//_______//

export const findNewActionsState = atomFamily<
  UnknownActionForUnknownElement[] | null,
  Phase
>({
  key: `timeline_newActions`,
  default: null,
})

export type IncompleteUnknownAction = {
  [Key in keyof UnknownRelationAction]: Key extends `id`
    ? string | undefined
    : UnknownRelationAction[Key]
}

export const findNewRelationActionsState = atomFamily<
  IncompleteUnknownAction[] | null,
  Phase
>({
  key: `timeline_newRelationActions`,
  default: null,
})

export const findChangeBatchesState = atomFamily<
  UnknownChangeForUnknownElement[][],
  Phase
>({
  key: `timeline_changeBatches`,
  default: [],
})

export const findMarkerState = atomFamily<number, Phase>({
  key: `timeline_marker`,
  default: -1,
})

//¯¯¯¯¯¯¯//
// TYPES //
//_______//

export type AnyElementName = keyof Elements

// actions

export type ActionForTheElementFamily_ = ActionFor_<keyof Elements, Elements>

export type NodeActionTo_ = ActionForTheElementFamily_[`NODE`]
export type EdgeActionTo_ = ActionForTheElementFamily_[`EDGE`]
export type AreaActionTo_ = ActionForTheElementFamily_[`AREA`]
export type PanelActionTo_ = ActionForTheElementFamily_[`PANEL`]

export type UnknownElementActionTo_ = ActionForTheElementFamily_[AnyElementName]

export type UnknownNodeAction = NodeActionTo_[ActionType]
export type UnknownEdgeAction = EdgeActionTo_[ActionType]
export type UnknownAreaAction = AreaActionTo_[ActionType]
export type UnknownPanelAction = PanelActionTo_[ActionType]

export type UnknownRelationAction = UnknownElementActionTo_[`ATTACH` | `DETACH`]
export type UnknownActionForUnknownElement = UnknownElementActionTo_[ActionType]

// changes

export type ChangeForTheElementFamily_ = ChangeFor_<keyof Elements, Elements>

export type NodeChangeTo_ = ChangeForTheElementFamily_[`NODE`]
export type EdgeChangeTo_ = ChangeForTheElementFamily_[`EDGE`]
export type AreaChangeTo_ = ChangeForTheElementFamily_[`AREA`]
export type PanelChangeTo_ = ChangeForTheElementFamily_[`PANEL`]

export type UnknownElementChangeTo_ = ChangeForTheElementFamily_[AnyElementName]

export type UnknownNodeChange = NodeChangeTo_[ActionType]
export type UnknownEdgeChange = EdgeChangeTo_[ActionType]
export type UnknownAreaChange = AreaChangeTo_[ActionType]
export type UnknownPanelChange = PanelChangeTo_[ActionType]

export type UnknownChangeForUnknownElement = UnknownElementChangeTo_[ActionType]

export type UnknownElementChange_DEPRECATED = {
  label: string
  undo: UnknownActionForUnknownElement
  redo: UnknownActionForUnknownElement
}

const myRelateAction: UnknownActionForUnknownElement = {
  type: `ATTACH`,
  family: `NODE_EDGE`,
  id: ``,
  relatedId: ``,
}
